<template>
    <div class="exchange-container">
        <div class="header">
            <h2>用户兑换管理</h2>
            <div class="header-actions">
                <!-- 状态筛选 -->
                <el-select
                    v-model="statusFilter"
                    placeholder="全部状态"
                    clearable
                    style="width: 140px; margin-right: 12px;"
                    @change="handleStatusChange"
                >
                    <el-option label="未兑换" value="false" />
                    <el-option label="已完成" value="true" />
                    <el-option label="已取消" value="cancel" />
                </el-select>
                <!-- 搜索框：实时过滤（@input） -->
                <el-input
                    v-model="searchText"
                    placeholder="搜索UID或用户名称"
                    :prefix-icon="Search"
                    clearable
                    style="width: 200px; margin-right: 12px;"
                    @input="handleSearchInput"
                />
                <el-button type="primary" @click="fetchExchangeList" :loading="refreshLoading" :icon="Refresh">
                    刷新数据
                </el-button>
            </div>
        </div>

        <el-card shadow="never" class="table-card">
            <el-table 
                :data="pagedData"           <!-- 绑定前端分页后的数据 -->
                border 
                style="width: 100%" 
                v-loading="tableLoading"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }" 
                stripe
            >
                <el-table-column prop="id" label="ID" width="100" align="center" />
                <el-table-column prop="uid" label="UID" width="120" align="center" />
                <el-table-column prop="name" label="用户名称" width="150" />
                <el-table-column prop="time" label="兑换时间" width="180" align="center" />
                <el-table-column prop="shop" label="兑换商品" />
                <el-table-column prop="virtual" label="类型" width="90" align="center">
                    <template #default="scope">
                        <el-tag :type="scope.row.virtual === '1' ? 'primary' : 'success'" size="small">
                            {{ scope.row.virtual === '1' ? '电子礼物' : '实物' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="exchange" label="状态" width="120" align="center">
                    <template #default="scope">
                        <el-tag 
                            :type="getStatusType(scope.row.exchange)" 
                            size="small"
                            effect="light"
                        >
                            {{ getStatusText(scope.row.exchange) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="250" align="center" fixed="right">
                    <template #default="scope">
                        <el-tooltip content="查看详情" placement="top">
                            <el-button type="primary" size="small" @click="showDetail(scope.row)" circle>
                                <el-icon><View /></el-icon>
                            </el-button>
                        </el-tooltip>
                        <el-tooltip content="标记为已完成" placement="top">
                            <el-button 
                                type="success" 
                                size="small" 
                                :loading="scope.row.completeLoading"
                                @click="handleComplete(scope.row)" 
                                circle
                                :disabled="scope.row.exchange === 'true' || scope.row.exchange === 'cancel' || scope.row.exchange === '' || !scope.row.exchange"
                            >
                                <el-icon><Check /></el-icon>
                            </el-button>
                        </el-tooltip>
                        <el-tooltip content="取消兑换" placement="top">
                            <el-button 
                                type="warning" 
                                size="small" 
                                :loading="scope.row.cancelLoading"
                                @click="handleCancel(scope.row)" 
                                circle
                                :disabled="scope.row.exchange === 'true' || scope.row.exchange === 'cancel' || scope.row.exchange === '' || !scope.row.exchange"
                            >
                                <el-icon><Close /></el-icon>
                            </el-button>
                        </el-tooltip>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件：总条数随过滤结果变化 -->
            <div class="pagination" v-if="filteredData.length > 0">
                <el-pagination 
                    v-model:current-page="currentPage" 
                    v-model:page-size="pageSize"
                    :page-sizes="[10, 20, 50, 100]" 
                    layout="total, sizes, prev, pager, next, jumper" 
                    :total="filteredData.length"
                    @size-change="handlePageChange" 
                    @current-change="handlePageChange" 
                />
            </div>
        </el-card>

        <!-- 详情弹窗 -->
        <el-dialog v-model="detailVisible" title="兑换详情" width="600px">
            <el-descriptions :column="1" border>
                <el-descriptions-item label="ID">{{ currentDetail.id }}</el-descriptions-item>
                <el-descriptions-item label="用户UID">{{ currentDetail.uid }}</el-descriptions-item>
                <el-descriptions-item label="用户名称">{{ currentDetail.name }}</el-descriptions-item>
                <el-descriptions-item label="兑换时间">{{ currentDetail.time }}</el-descriptions-item>
                <el-descriptions-item label="兑换商品">{{ currentDetail.shop }}</el-descriptions-item>
                <el-descriptions-item label="兑换状态">
                    <el-tag :type="getStatusType(currentDetail.exchange)" size="small" effect="light">
                        {{ getStatusText(currentDetail.exchange) }}
                    </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="收货人">{{ currentDetail.received }}</el-descriptions-item>
                <el-descriptions-item label="收货地址">{{ currentDetail.receivedurl }}</el-descriptions-item>
                <el-descriptions-item label="联系电话">{{ currentDetail.receivedph }}</el-descriptions-item>
            </el-descriptions>
            <template #footer>
                <el-button type="primary" @click="detailVisible = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Check, Refresh, View, Search, Close } from '@element-plus/icons-vue';

// 搜索关键字
const searchText = ref('');

// 状态筛选
const statusFilter = ref('');

// 表格原始数据（全部记录）
const tableData = ref([]);
const tableLoading = ref(false);
const refreshLoading = ref(false);

// 分页参数
const currentPage = ref(1);
const pageSize = ref(10);

// 详情弹窗
const detailVisible = ref(false);
const currentDetail = ref({
    id: "",
    uid: "",
    name: "",
    time: "",
    shop: "",
    exchange: "",
    received: "",
    receivedurl: "",
    receivedph: ""
});

// 计算属性：根据搜索关键字和状态筛选数据
const filteredData = computed(() => {
    let result = tableData.value;
    
    // 状态筛选
    if (statusFilter.value) {
        result = result.filter(item => item.exchange === statusFilter.value);
    }
    
    // 搜索关键字筛选（不区分大小写）
    if (searchText.value) {
        const keyword = searchText.value.toLowerCase().trim();
        result = result.filter(item => 
            item.uid.toLowerCase().includes(keyword) || 
            item.name.toLowerCase().includes(keyword)
        );
    }
    
    return result;
});

// 计算属性：对过滤后的数据做分页
const pagedData = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value;
    const end = start + pageSize.value;
    return filteredData.value.slice(start, end);
});

// 获取状态标签类型
const getStatusType = (status) => {
    // 处理空值、null、undefined 视为未兑换
    if (!status || status === '' || status === 'false') {
        return 'info';    // 未兑换 - 灰色
    }
    switch (status) {
        case 'true':
            return 'success';  // 已完成 - 绿色
        case 'cancel':
            return 'warning'; // 已取消 - 橙色
        default:
            return 'info';    // 未兑换 - 灰色
    }
};

// 获取状态文本
const getStatusText = (status) => {
    // 处理空值、null、undefined 视为未兑换
    if (!status || status === '' || status === 'false') {
        return '未兑换';
    }
    switch (status) {
        case 'true':
            return '已完成';
        case 'cancel':
            return '已取消';
        default:
            return '未兑换';
    }
};

// 输入搜索时重置到第一页
const handleSearchInput = () => {
    currentPage.value = 1;
};

// 状态筛选变化时重置到第一页
const handleStatusChange = () => {
    currentPage.value = 1;
};

// 分页变化时的处理（可留空，因为已通过 v-model 自动更新页码，pagedData 自动重新计算）
const handlePageChange = () => {
    // 可以留空，或者执行一些额外操作
};

// 获取全部兑换列表数据（移除分页参数，改为获取全部）
const fetchExchangeList = async () => {
    tableLoading.value = true;
    try {
        // 注意：如果数据量很大，建议后端支持一次性返回所有记录，或设置一个足够大的 size
        const response = await axios.get('/adminapi/user/Exchange/list', {
            params: {
                // 不再传递 page 和 size，或传递 size=9999 获取所有数据（根据后端支持情况调整）
                size: 9999  // 假设后端允许通过 size 参数控制返回数量
            }
        });

        if (response.data) {
            // 假设返回的数据结构为 { results: [...] } 或直接是数组
            const results = response.data.results || response.data;
            tableData.value = results.map(item => ({
                ...item,
                completeLoading: false,
                cancelLoading: false
            }));
        } else {
            ElMessage.error('获取兑换列表失败');
        }
    } catch (error) {
        console.error('请求兑换列表出错：', error);
        ElMessage.error('网络异常，获取兑换列表失败');
    } finally {
        tableLoading.value = false;
        refreshLoading.value = false;
    }
};

// 显示详情弹窗
const showDetail = (row) => {
    currentDetail.value = { ...row };
    detailVisible.value = true;
};

// 处理"完成"操作
const handleComplete = async (row) => {
    try {
        await ElMessageBox.confirm('确认将该兑换记录标记为已完成吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        });
        
        row.completeLoading = true;
        // 写操作走 POST（后端路由已从 GET 改过来）
        const response = await axios.post(`/adminapi/Exchange/over/${row.id}`);
        if (response.data && response.data.code === 200) {
            ElMessage.success('标记完成成功');
            fetchExchangeList();  // 刷新全部数据
        } else {
            ElMessage.error(response.data?.message || '标记完成失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('请求完成操作出错：', error);
            ElMessage.error('网络异常，标记完成失败');
        }
    } finally {
        row.completeLoading = false;
    }
};

// 处理"取消兑换"操作
const handleCancel = async (row) => {
    try {
        await ElMessageBox.confirm('取消兑换将退还用户消耗的积分，是否继续？', '警告', {
            confirmButtonText: '确定取消',
            cancelButtonText: '返回',
            type: 'warning'
        });
        
        row.cancelLoading = true;
        // 写操作走 POST（后端路由已从 GET 改过来）
        const response = await axios.post(`/adminapi/Exchange/cancel/${row.id}`);
        if (response.data && response.data.code === 200) {
            const refundAmount = response.data.data?.refundAmount || 0;
            ElMessage.success(`兑换已取消，退还积分：${refundAmount}`);
            fetchExchangeList();  // 刷新全部数据
        } else {
            ElMessage.error(response.data?.message || '取消兑换失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('请求取消操作出错：', error);
            ElMessage.error('网络异常，取消兑换失败');
        }
    } finally {
        row.cancelLoading = false;
    }
};

onMounted(() => {
    fetchExchangeList();
});
</script>

<style scoped>
/* 样式保持不变 */
.exchange-container {
    padding: 20px;
    background-color: #f0f2f5;
    min-height: calc(100vh - 60px);
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}

.header h2 {
    color: #333;
    font-weight: 600;
    margin: 0;
}

.header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.table-card {
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    border: none;
}

.el-table {
    margin-top: 0;
    font-size: 14px;
}

.el-table :deep(.el-table__cell) {
    padding: 12px 0;
}

.pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.el-button+.el-button {
    margin-left: 8px;
}

.el-icon {
    vertical-align: middle;
}

:deep(.el-descriptions) {
    margin-top: 20px;
}

:deep(.el-descriptions__label) {
    width: 120px;
    font-weight: bold;
    color: #606266;
}

:deep(.el-descriptions__content) {
    color: #303133;
}
</style>